// ============================================================
// Section Personas (Statistiques) : fonctions pures, sans I/O ni store
// ============================================================
// Données du satellite (routes /api/stats/gameplay/personas et /api/stats/gameplay/personas/player/{uuid}) : pour
// chaque joueur, une part par persona (Joueur PvP, Joueur Farmer, Mineur...) qui fait 100 %, calculée sur les centiles
// de ses indicateurs ; la répartition globale ; la fiche d'un joueur avec les indicateurs qui expliquent ses parts.

import type { PersonaDTO, PersonaIndicatorDTO, PersonaPlayerRowDTO } from '@/services/api.service';

/** Version du satellite qui sert la section : la page le dit face à un satellite plus ancien. */
export const PERSONAS_SATELLITE_VERSION = '1.0.31';

/** Périodes proposées pour les indicateurs datés (le satellite en accepte 90 au plus). */
export const PERSONA_PERIODS = [7, 30, 90] as const;

/**
 * Couleur d'un persona : elle suit le persona (sa place dans la liste du satellite), jamais son classement. Huit teintes
 * validées de la section Métiers ; au-delà, du gris. La couleur n'est jamais seule : chaque part a son nom et son %.
 */
export function personaColor(id: string, all: readonly string[]): string {
  const i = all.indexOf(id);
  return i >= 0 && i < 8 ? `var(--persona-${i + 1})` : 'var(--persona-other)';
}

/** « 62 % » ; une part non entière garde une décimale (moyennes de la répartition). */
export function formatPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
}

/** Valeur d'un indicateur selon son unité : argent, XP, nombre, ou oui/non. */
export function formatIndicatorValue(unit: string, value: number): string {
  if (unit === 'flag') return value > 0 ? 'Oui' : 'Non';
  const n = Math.round(value).toLocaleString('fr-FR');
  if (unit === 'money') return `${n} $`;
  if (unit === 'xp') return `${n} XP`;
  return n;
}

/** Fenêtre d'un indicateur, en mots. */
export function windowLabel(window: string, days: number): string {
  if (window === 'period') return `${days} derniers jours`;
  if (window === 'cumul') return 'depuis la mise en service';
  if (window === 'current') return 'aujourd\'hui';
  return window;
}

/** Nom d'une base lue par le satellite, pour l'utilisateur du panel. */
export function sourceLabel(source: string): string {
  const names: Record<string, string> = {
    vault: 'Argent (Vault)',
    jobs: 'Métiers',
    palaprofil: 'PalaProfil',
    topluck: 'TopLuck (minage)',
    factionapi: 'Factions',
    moderation: 'Sanctions',
  };
  return names[source] ?? source;
}

/** Message d'erreur de la liste, face à un satellite qui ne sert pas la section, à un refus ou à une panne. */
export function personasError(status: number | undefined, error: string | undefined): string {
  if (status === 404) {
    return `Le satellite de ce projet ne sert pas encore les personas : cela arrive avec le satellite ${PERSONAS_SATELLITE_VERSION}.`;
  }
  if (status === 403) return "Ton grade n'a pas accès à cette section des statistiques.";
  if (status === 503) return error?.trim() || 'Une base du satellite ne répond pas : les personas ne peuvent pas être calculés.';
  return error?.trim() || "Les personas n'ont pas pu être lus.";
}

/** Une part d'une barre empilée : le persona, sa part entière et sa couleur. */
export interface ShareSegment {
  id: string;
  label: string;
  share: number;
  color: string;
}

/** Parts d'un joueur, dans l'ordre des personas, sans les parts nulles : la barre fait 100 %. */
export function shareSegments(row: Pick<PersonaPlayerRowDTO, 'shares'>, personas: readonly Pick<PersonaDTO, 'id' | 'label'>[]): ShareSegment[] {
  const ids = personas.map((p) => p.id);
  return personas
    .map((p) => ({ id: p.id, label: p.label, share: row.shares[p.id] ?? 0, color: personaColor(p.id, ids) }))
    .filter((s) => s.share > 0);
}

/** « 62 % Joueur PvP, 25 % Joueur Farmer, 13 % Marchand » : les parts d'un joueur en mots, de la plus grande à la plus petite. */
export function sharesSentence(row: Pick<PersonaPlayerRowDTO, 'shares'>, personas: readonly Pick<PersonaDTO, 'id' | 'label'>[]): string {
  const parts = shareSegments(row, personas).sort((a, b) => b.share - a.share);
  if (parts.length === 0) return 'Rien de mesuré';
  return parts.map((s) => `${s.share} % ${s.label}`).join(', ');
}

/** Ce que dit l'activité mesurée (0 à 100) : sur combien de jeu reposent les parts. */
export function activityLabel(activity: number): string {
  if (activity >= 40) return 'Très actif';
  if (activity >= 20) return 'Actif';
  if (activity >= 8) return 'Peu mesuré';
  return 'Très peu mesuré';
}

/** Indicateurs indisponibles (base absente ou injoignable), groupés par raison : de quoi les dire en une ligne chacun. */
export function missingByReason(indicators: readonly PersonaIndicatorDTO[]): { reason: string; labels: string[] }[] {
  const out = new Map<string, string[]>();
  for (const i of indicators) {
    if (i.available) continue;
    const reason = i.reason?.trim() || 'Indisponible';
    out.set(reason, [...(out.get(reason) ?? []), i.label]);
  }
  return [...out.entries()].map(([reason, labels]) => ({ reason, labels }));
}

/** Persona le plus représenté (part moyenne la plus haute), ou null sans joueur mesuré. */
export function leadingPersona(personas: readonly PersonaDTO[]): PersonaDTO | null {
  let best: PersonaDTO | null = null;
  for (const p of personas) if (p.averageShare > 0 && (!best || p.averageShare > best.averageShare)) best = p;
  return best;
}
